import React, { useMemo, memo } from "react";
import type { BiddingInfo } from "../../../types";
import type { ListingCategoryType } from "../../../constants";
import { BiddingItemStatusConst } from "../../../constants";
import { BiddingInputForm } from "./BiddingInputForm"; // 入札フォーム
import { BiddingItemDetails } from "./BiddingItemDetails"; // 詳細表示
import { useTruncatedTooltip } from "../../common/TruncatedTooltip";
import { Heart } from "lucide-react";
import Big from "big.js";
import { useAuth } from "../../../contexts/AuthContext";
import { useToggleFavoriteMutation } from "../../../hooks/useBiddingItemsQuery";
// 一覧行用Props
interface BiddingInputItemRowProps {
  item: BiddingInfo;
  isSelected: boolean;
  isSaving?: boolean;
  listingCategory: ListingCategoryType;
  isFavorite:boolean;
  onToggleSelect: () => void;
  onSubmitBid: (bidData: {
    itemId: string;
    branchNumber: number;
    bidQuantity1: number;
    bidQuantity2: number;
    bidPrice: number;
  }) => void;
}

// # 一覧の各行を表すコンポーネント ---
export const BiddingInputItemRow: React.FC<BiddingInputItemRowProps> = ({
  item,
  isSelected,
  isSaving = false,
  listingCategory,
  isFavorite,
  onToggleSelect,
  onSubmitBid,
}) => {

  const { currentUser } = useAuth();
  const loginId = currentUser?.loginId || "";
  
  // 更新用Mutation
  const { mutate, isPending } = useToggleFavoriteMutation(loginId);

  // 「確定済のみ押下可能」 = 「確定済じゃなければ無効」
  //const isBidDisabled = item.status !== BiddingItemStatusConst.CONFIRMED;

  //----------------
  // Memo定義
  //----------------
  //生産者名を表示してよいかどうかの判定（買手専用シンプル版）
  const canSeeFarmerName = useMemo(() => {
    // Hidden(隠す)が true なら判定は false (表示しない)
    return !item.isFarmerHidden;
  }, [item.isFarmerHidden]);
  const displayInfo = useMemo(() => {
    try {
      // 1. 各値を Big オブジェクトに変換
      const c1 = new Big(item.capacity1 || 0);
      const c2 = new Big(item.capacity2 || 0);
      const q1 = new Big(item.quantity1 || 0);
      const q2 = new Big(item.quantity2 || 0);
      const bQ1 = new Big(item.bidQuantity1 || 0);
      const bQ2 = new Big(item.bidQuantity2 || 0);

      // 2. 10進数で正確に計算を実行
      // 総容量: (c1 * q1) + (c2 * q2)
      const total = c1.times(q1).plus(c2.times(q2));

      // 入札容量: (c1 * bQ1) + (c2 * bQ2)
      const bid = c1.times(bQ1).plus(c2.times(bQ2));

      return {
        totalCapacity: total.toNumber(),
        bidCapacity: bid.toNumber(),
      };
    } catch (e) {
      // 数値として読み込めない値が入った場合のセーフティ
      return { totalCapacity: 0, bidCapacity: 0 };
    }
    // 3. 計算に使用しているすべての値を依存配列に含める
  }, [
    item.capacity1,
    item.quantity1,
    item.capacity2,
    item.quantity2,
    item.bidQuantity1,
    item.bidQuantity2,
  ]);

  //----------------
  // その他定数定義
  //----------------
  const rowClasses = "hover:bg-slate-50";
  const { tooltipProps, TooltipElement } = useTruncatedTooltip();

  // 「提出済」か「確定済」の場合のみオブジェクトを取得 (それ以外は undefined)
  let displayStatus = { label: "", color: "" };
  if (item.status === BiddingItemStatusConst.APPLIED) {
    // 提出済 = 出品予定
    displayStatus = {
      label: "出品予定",
      color: "bg-red-100 text-red-800",
    };
  } else if (item.status === BiddingItemStatusConst.CONFIRMED) {
    // 確定済 = 受付中
    displayStatus = {
      label: "出品中",
      color: "bg-blue-100 text-blue-800",
    };
  }
  console.log(displayStatus); //使用していないビルドエラー回避

  // お気に入りボタンクリック時の処理
  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation(); 
    if (isPending || !loginId) return;

    // Mutationを実行
    mutate({ 
      itemId: item.itemId, 
      branchNumber: item.branchNumber 
    });
  };

  //----------------
  // レンダリング処理
  //----------------
  return (
    <>
      {/* 全体のコンテナ */}
      <div
        className={`mt-2 lg:mt-4 border-b lg:pb-0 border-slate-800 lg:border lg:rounded-lg lg:shadow-sm overflow-hidden bg-white ${isSelected ? "ring-2 ring-indigo-500 rounded-lg" : ""}`}
      >
        {/* --- クリック可能な行ヘッダー --- */}
        <div
          className={`lg:p-4 ${rowClasses} transition-colors cursor-pointer relative`}
          onClick={onToggleSelect}
        >
          <div className="flex flex-col sm:flex-row sm:items-start">
            {/* --- 表示内容エリア --- */}
            <div className="flex-1 grid grid-cols-6 lg:grid-cols-15 gap-x-2 gap-y-1 sm:gap-x-4 items-baseline lg:items-start pr-8 sm:pr-0">
              {/* --- 1. お気に入りボタン (Heart) --- */}
              <div className="col-span-1 order-1 lg:order-none lg:col-span-1 min-w-[3rem] flex flex-col items-center">
                <button 
                  onClick={handleToggleFavorite}
                  disabled={isPending}
                  className="translate-y-2 lg:translate-y-2 p-1 -ml-2 lg:-ml-6 rounded-full hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50"
                  type="button"
                >
                <Heart className={`... ${isFavorite ? "fill-red-400 text-red-400" : "text-slate-400"}`} />
                </button>
              </div>

              {/* --- 2. 入札番号 --- */}
              <div className="col-span-1 order-2 lg:order-none lg:col-span-1 min-w-[3rem]">
                <p className="hidden lg:block text-base text-slate-500 lg:-ml-4">
                  入札番号
                </p>
                <p className="text-base font-bold text-left lg:-ml-4">
                  {item.biddingNumber}
                </p>
              </div>

              {/* --- PCのみ表示 (茶期・茶種・品種) --- */}
              <div className="hidden lg:block lg:col-span-1">
                <p className="text-base text-slate-500">茶期</p>
                <p {...tooltipProps} className="text-lg font-bold truncate">
                  {item.teaSeasonName}
                </p>
              </div>
              <div className="hidden lg:block lg:col-span-1">
                <p className="text-base text-slate-500">茶種</p>
                <p {...tooltipProps} className="text-lg font-bold truncate">
                  {item.teaTypeName}
                </p>
              </div>
              <div className="hidden lg:block lg:col-span-1">
                <p className="text-base text-slate-500">品種名</p>
                <p {...tooltipProps} className="text-lg font-bold truncate">
                  {item.varietyName}
                </p>
              </div>

              {/* --- 4. 売手 --- */}
              <div className="col-span-4 order-3 lg:order-none lg:col-span-2 flex flex-col items-start lg:mt-0">
                <p className="hidden lg:block text-base text-slate-500">売手</p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg lg:font-bold truncate max-w-full lg:w-fit"
                >
                  {item.sellerName}
                </p>
              </div>

              {/* --- 5. 生産者 --- */}
              <div className="col-span-4 order-5 lg:order-none lg:col-span-2 flex flex-col items-start lg:mt-0">
                <p className="hidden lg:block text-base text-slate-500">
                  生産者
                </p>
                <p
                  // 非表示のときはツールチップ（ホバーで中身が出る機能）を無効化する
                  {...(canSeeFarmerName ? tooltipProps : {})}
                  className="text-base lg:text-lg lg:font-bold truncate max-w-full lg:w-fit"
                >
                  {canSeeFarmerName ? item.farmerName : "-"}
                </p>
              </div>

              {/* --- 6. 荷印 --- */}
              <div className="col-span-2 order-4 lg:order-none lg:col-span-2 text-left lg:pl-4">
                <p className="hidden lg:block text-base text-slate-500">荷印</p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg text-left lg:font-bold truncate ml-2.5 lg:ml-0"
                >
                  {item.brand}
                </p>
              </div>

              <div className="hidden lg:block lg:order-none lg:col-span-2 text-left lg:pl-4">
                <p className="hidden lg:block text-base text-slate-500">
                  総量(kg)
                </p>
                <p className="text-base lg:text-lg text-left lg:font-bold truncate ml-2.5 lg:ml-0">
                  {displayInfo.totalCapacity}
                </p>
              </div>

              {/* --- 7. 入札額表示 (PCではここに並べる) --- */}
              <div className="hidden lg:flex lg:col-span-2 flex-col items-end ">
                <p className="text-base text-slate-500">現在の入札額</p>
                <p
                  className={`text-lg font-black ${item.bidPrice > 0 ? "text-indigo-600" : "text-slate-400"}`}
                >
                  {item.bidPrice > 0
                    ? `${item.bidPrice.toLocaleString()} 円`
                    : "未入札"}
                </p>
              </div>
            </div>

            {/* モバイル用：入札額表示 */}
            <div className="lg:hidden w-full mt-3">
              {/* w-fullで横幅 pyで高さ */}
              <div className="flex items-baseline justify-between w-full bg-slate-100 rounded-lg py-2">
                {/* 左側：現在入札額 */}
                <div className="flex-1 text-right">
                  <span className="text-xl font-bold text-blue-600 tracking-wider">
                    {item.bidPrice > 0
                      ? item.bidPrice.toLocaleString()
                      : "未入札"}
                  </span>
                  {item.bidPrice > 0 && (
                    <span className="text-sm font-normal text-slate-500 ml-1">
                      円
                    </span>
                  )}
                </div>
                <div className="px-4 text-slate-800 font-light text-xl">/</div>
                {/* 右側：最低落札値 */}
                <div className="flex-1 text-left">
                  <span className="text-xl font-bold text-slate-900 tracking-wider">
                    {Number(item.bidMinPrice).toLocaleString()}
                  </span>
                  <span className="text-sm font-normal text-slate-500 ml-1">
                    円
                  </span>
                </div>
              </div>
            </div>

            {/* モバイル用: 隠れている項目の簡易表示 */}
            <div className="md:hidden py-2 text-base flex flex-wrap gap-x-3 pointer-events-none justify-left ml-2.5 lg:mt-2">
              <span>{item.teaSeasonName}</span>
              <span>/</span>
              <span>{item.teaTypeName}</span>
              <span>/</span>
              <span>{item.varietyName}</span>
              <span>/</span>
              <span>{displayInfo.totalCapacity}kg</span>
            </div>

            {/* --- モバイル用 展開アイコン --- */}
            <div className="absolute top-4 right-4 sm:hidden pointer-events-none">
              <svg
                className={`w-6 h-6 text-slate-400 transition-transform ${isSelected ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* --- 詳細情報・入札フォームエリア --- */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${isSelected ? "max-h-[1200px]" : "max-h-0"}`}
        >
          {isSelected && (
            <div className="border-t border-slate-200">
              {/* 詳細情報 */}
              <BiddingItemDetails item={item} displayInfo={displayInfo} />
              {/* 入札フォーム */}
              <BiddingInputForm
                item={item}
                onSubmit={onSubmitBid}
                isSaving={isSaving}
                listingCategory={listingCategory}
              />
            </div>
          )}
        </div>
      </div>
      {TooltipElement}
    </>
  );
};

// React.memo でラップ
export default memo(BiddingInputItemRow);
